// A row dragged sideways to act on it. A committed swipe either carries the
// row away (it slides off its side, its gap closes, then the action runs) or
// returns it to rest once the action has run. An action may reveal a coloured
// panel behind the row; without one the row fades as it travels.
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  type ComponentType,
  type ReactNode,
  type SVGProps,
} from "react";
import { motionMs, motionTransition, useSwipe, type SwipeDirection } from "@/platform/client";
import { cx } from "@/platform/ui/primitives/index.js";

type Action = () => void | Promise<unknown>;

interface SwipeAction {
  /** A rejected promise brings a row that left back into place. */
  onTrigger: Action;
  /** Carry the row away on commit; it returns to rest otherwise. */
  exits?: boolean;
  /** The panel shown behind the row while it is dragged. */
  reveal?: {
    icon: ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;
    label: string;
    /** CSS color value, e.g. "var(--color-negative)". */
    color: string;
  };
}

export interface SwipeableRowHandle {
  /** Carry the row off and close its gap, then run `action`: how a button
   *  removes the row the way a swipe would. */
  leave(action: Action): void;
}

/** Runs `action`, calling `failed` if it throws or its promise rejects. */
function attempt(action: Action, failed: () => void) {
  try {
    void Promise.resolve(action()).catch(failed);
  } catch {
    failed();
  }
}

/** Closes the gap a row leaves, then calls `done`. */
function collapse(el: HTMLElement | null, done: () => void) {
  const ms = motionMs();
  if (!el || ms === 0) {
    if (el) el.style.display = "none";
    return done();
  }
  el.style.overflow = "hidden";
  el.style.height = `${el.offsetHeight}px`;
  void el.offsetHeight; // the transition starts from the measured height
  el.style.transition = motionTransition("height", "padding", "margin", "border-width");
  Object.assign(el.style, {
    height: "0px",
    paddingTop: "0px",
    paddingBottom: "0px",
    marginTop: "0px",
    marginBottom: "0px",
    borderTopWidth: "0px",
    borderBottomWidth: "0px",
  });
  setTimeout(done, ms);
}

const ActionPanel = forwardRef<
  HTMLDivElement,
  { reveal: NonNullable<SwipeAction["reveal"]>; side: "left" | "right"; dragging: boolean }
>(function ActionPanel({ reveal, side, dragging }, ref) {
  const Icon = reveal.icon;
  return (
    <div
      ref={ref}
      aria-hidden
      className={cx(
        "pointer-events-none absolute inset-y-0 flex w-full items-center gap-2 px-4 text-sm font-bold text-bg",
        side === "left" ? "justify-start" : "justify-end",
      )}
      style={{
        background: reveal.color,
        opacity: 0,
        transition: dragging ? "none" : motionTransition("opacity"),
      }}
    >
      <Icon size={16} />
      {reveal.label}
    </div>
  );
});

export const SwipeableRow = forwardRef<
  SwipeableRowHandle,
  {
    /** Triggered when the row is dragged toward the left past the threshold. */
    onLeft?: SwipeAction;
    /** Triggered when the row is dragged toward the right past the threshold. */
    onRight?: SwipeAction;
    disabled?: boolean;
    children: ReactNode;
    /** On the outer element, which closes when the row leaves: spacing
     *  between rows belongs here, so it closes with the row. */
    className?: string;
  }
>(function SwipeableRow({ onLeft, onRight, disabled, children, className }, handle) {
  const outer = useRef<HTMLDivElement>(null);
  // Dragging right uncovers the panel on the left edge, and the reverse.
  const leftPanel = useRef<HTMLDivElement>(null);
  const rightPanel = useRef<HTMLDivElement>(null);
  // A button's action, run in place of the swipe's when the row leaves.
  const pending = useRef<Action | null>(null);
  const actionFor = (d: SwipeDirection) => (d === "left" ? onLeft : onRight);

  const swipe = useSwipe<HTMLDivElement>({
    directions: (["left", "right"] as const).filter((d) => actionFor(d)),
    exits: (d) => actionFor(d)?.exits ?? false,
    fade: (d) => !actionFor(d)?.reveal,
    disabled,
    onProgress(p) {
      if (leftPanel.current)
        leftPanel.current.style.opacity = p > 0 ? String(Math.min(1, 0.35 + p * 0.65)) : "0";
      if (rightPanel.current)
        rightPanel.current.style.opacity = p < 0 ? String(Math.min(1, 0.35 - p * 0.65)) : "0";
    },
    onCommit(d) {
      const action = pending.current ?? actionFor(d)?.onTrigger;
      const exits = pending.current != null || actionFor(d)?.exits;
      pending.current = null;
      if (!action) return;
      if (!exits) return attempt(action, () => {});
      collapse(outer.current, () =>
        attempt(action, () => {
          outer.current?.removeAttribute("style");
          swipe.reset();
        }),
      );
    },
  });

  useImperativeHandle(handle, () => ({
    leave(action) {
      pending.current = action;
      swipe.leave("right");
    },
  }));

  const dragging = swipe.phase === "drag";
  return (
    <div ref={outer} className={className}>
      <div
        className="relative overflow-hidden rounded-lg"
        style={{ touchAction: "pan-y" }}
        {...swipe.handlers}
      >
        {onRight?.reveal && (
          <ActionPanel ref={leftPanel} reveal={onRight.reveal} side="left" dragging={dragging} />
        )}
        {onLeft?.reveal && (
          <ActionPanel ref={rightPanel} reveal={onLeft.reveal} side="right" dragging={dragging} />
        )}
        {/* Above the panels, which lie beneath it and never take a pointer. */}
        <div ref={swipe.ref} className="relative bg-surface">
          {children}
        </div>
      </div>
    </div>
  );
});
